import React, { useState } from "react";
import {
  Moon,
  Sun,
  X,
} from "lucide-react";
import { useLanguageStore } from "../store/languageStore";
import { useUserPreferencesStore } from "../store/userPreferencesStore";
import { useDarkModeStore } from "../store/darkModeStore";
import { useAnalytics } from "../utils/analytics";
import AnimatedButton from "./AnimatedButton";

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose }) => {
  const { selectedLanguage } = useLanguageStore();
  const { isDarkMode, toggleDarkMode } = useDarkModeStore();
  const { trackFeatureUsage } = useAnalytics();
  const [activeTab, setActiveTab] = useState<
    "accessibility" | "interface"
  >("interface");

  const {
    accessibility,
    interface: interfacePrefs,
    updateAccessibility,
    updateInterface,
  } = useUserPreferencesStore();

  const messages = {
    EN: {
      title: "Settings",
      tabs: {
        accessibility: "Accessibility",
        interface: "Interface",
      },
      accessibility: {
        reducedMotion: "Reduce Motion",
        reducedMotionDesc: "Minimize animations and transitions",
        fontSize: "Font Size",
        screenReader: "Screen Reader Optimized",
        screenReaderDesc: "Enhanced compatibility with assistive technologies",
        keyboardNav: "Keyboard Navigation",
        keyboardNavDesc: "Full keyboard accessibility support",
      },
      interface: {
        theme: "Theme",
        compactMode: "Compact Mode",
        compactModeDesc: "Reduce spacing and padding",
        animations: "Animations",
        animationsDesc: "Enable interface animations",
        notifications: "Notifications",
        notificationsDesc: "Show system notifications",
        tooltips: "Tooltips",
        tooltipsDesc: "Show helpful tooltips",
        darkMode: "Dark Mode",
        lightMode: "Light Mode",
        fontSizes: {
          small: "Small",
          medium: "Medium",
          large: "Large"
        }
      },
      actions: {
        save: "Save Changes",
        reset: "Reset to Defaults",
        close: "Close",
      },
      notifications: {
        saved: "Settings saved successfully",
        reset: "Settings reset to defaults",
      },
    },
    ES: {
      title: "Configuración",
      tabs: {
        accessibility: "Accesibilidad",
        interface: "Interfaz",
      },
      accessibility: {
        reducedMotion: "Reducir Movimiento",
        reducedMotionDesc: "Minimizar animaciones y transiciones",
        fontSize: "Tamaño de Fuente",
        screenReader: "Optimizado para Lector de Pantalla",
        screenReaderDesc: "Compatibilidad mejorada con tecnologías de asistencia",
        keyboardNav: "Navegación por Teclado",
        keyboardNavDesc: "Soporte completo de accesibilidad por teclado",
      },
      interface: {
        theme: "Tema",
        compactMode: "Modo Compacto",
        compactModeDesc: "Reducir espaciado y relleno",
        animations: "Animaciones",
        animationsDesc: "Habilitar animaciones de interfaz",
        notifications: "Notificaciones",
        notificationsDesc: "Mostrar notificaciones del sistema",
        tooltips: "Tooltips",
        tooltipsDesc: "Mostrar tooltips útiles",
        darkMode: "Modo Oscuro",
        lightMode: "Modo Claro",
        fontSizes: {
          small: "Pequeño",
          medium: "Mediano",
          large: "Grande"
        }
      },
      actions: {
        save: "Guardar Cambios",
        reset: "Restablecer Valores Predeterminados",
        close: "Cerrar",
      },
      notifications: {
        saved: "Configuración guardada exitosamente",
        reset: "Configuración restablecida a valores predeterminados",
      },
    },
    ZH: {
      title: "设置",
      tabs: {
        accessibility: "无障碍",
        interface: "界面",
      },
      accessibility: {
        reducedMotion: "减少动画",
        reducedMotionDesc: "最小化动画和过渡效果",
        fontSize: "字体大小",
        screenReader: "屏幕阅读器优化",
        screenReaderDesc: "增强与辅助技术的兼容性",
        keyboardNav: "键盘导航",
        keyboardNavDesc: "完整的键盘无障碍支持",
      },
      interface: {
        theme: "主题",
        compactMode: "紧凑模式",
        compactModeDesc: "减少间距和内边距",
        animations: "动画",
        animationsDesc: "启用界面动画",
        notifications: "通知",
        notificationsDesc: "显示系统通知",
        tooltips: "工具提示",
        tooltipsDesc: "显示有用的工具提示",
        darkMode: "深色模式",
        lightMode: "浅色模式",
        fontSizes: {
          small: "小",
          medium: "中",
          large: "大"
        }
      },
      actions: {
        save: "保存更改",
        reset: "重置为默认值",
        close: "关闭",
      },
      notifications: {
        saved: "设置保存成功",
        reset: "设置已重置为默认值",
      },
    },
    VI: {
      title: "Cài đặt",
      tabs: {
        accessibility: "Khả năng truy cập",
        interface: "Giao diện",
      },
      accessibility: {
        reducedMotion: "Giảm chuyển động",
        reducedMotionDesc: "Tối thiểu hóa hoạt ảnh và chuyển tiếp",
        fontSize: "Cỡ chữ",
        screenReader: "Tối ưu hóa cho trình đọc màn hình",
        screenReaderDesc: "Tăng cường tương thích với công nghệ hỗ trợ",
        keyboardNav: "Điều hướng bàn phím",
        keyboardNavDesc: "Hỗ trợ đầy đủ khả năng truy cập bằng bàn phím",
      },
      interface: {
        theme: "Giao diện",
        compactMode: "Chế độ thu gọn",
        compactModeDesc: "Giảm khoảng cách và đệm",
        animations: "Hoạt ảnh",
        animationsDesc: "Bật hoạt ảnh giao diện",
        notifications: "Thông báo",
        notificationsDesc: "Hiển thị thông báo hệ thống",
        tooltips: "Gợi ý",
        tooltipsDesc: "Hiển thị gợi ý hữu ích",
        darkMode: "Chế độ tối",
        lightMode: "Chế độ sáng",
        fontSizes: {
          small: "Nhỏ",
          medium: "Trung bình",
          large: "Lớn"
        }
      },
      actions: {
        save: "Lưu thay đổi",
        reset: "Đặt lại mặc định",
        close: "Đóng",
      },
      notifications: {
        saved: "Đã lưu cài đặt thành công",
        reset: "Đã đặt lại cài đặt về mặc định",
      },
    },
    FIL: {
      title: "Mga Setting",
      tabs: {
        accessibility: "Accessibility",
        interface: "Interface",
      },
      accessibility: {
        reducedMotion: "Bawasan ang Motion",
        reducedMotionDesc: "I-minimize ang mga animation at transition",
        fontSize: "Laki ng Font",
        screenReader: "Screen Reader Optimized",
        screenReaderDesc: "Enhanced compatibility sa assistive technologies",
        keyboardNav: "Keyboard Navigation",
        keyboardNavDesc: "Full keyboard accessibility support",
      },
      interface: {
        theme: "Theme",
        compactMode: "Compact Mode",
        compactModeDesc: "Bawasan ang spacing at padding",
        animations: "Animations",
        animationsDesc: "I-enable ang interface animations",
        notifications: "Notifications",
        notificationsDesc: "Ipakita ang system notifications",
        tooltips: "Tooltips",
        tooltipsDesc: "Ipakita ang mga helpful tooltips",
        darkMode: "Madilim na Mode",
        lightMode: "Maliwanag na Mode",
        fontSizes: {
          small: "Maliit",
          medium: "Katamtaman",
          large: "Malaki"
        }
      },
      actions: {
        save: "I-save ang mga Pagbabago",
        reset: "I-reset sa Defaults",
        close: "Isara",
      },
      notifications: {
        saved: "Matagumpay na na-save ang mga setting",
        reset: "Na-reset ang mga setting sa defaults",
      },
    },
    KO: {
      title: "설정",
      tabs: {
        accessibility: "접근성",
        interface: "인터페이스",
      },
      accessibility: {
        reducedMotion: "모션 감소",
        reducedMotionDesc: "애니메이션 및 전환 효과 최소화",
        fontSize: "글꼴 크기",
        screenReader: "화면 읽기 최적화",
        screenReaderDesc: "보조 기술과의 향상된 호환성",
        keyboardNav: "키보드 탐색",
        keyboardNavDesc: "완전한 키보드 접근성 지원",
      },
      interface: {
        theme: "테마",
        compactMode: "컴팩트 모드",
        compactModeDesc: "간격과 패딩 줄이기",
        animations: "애니메이션",
        animationsDesc: "인터페이스 애니메이션 활성화",
        notifications: "알림",
        notificationsDesc: "시스템 알림 표시",
        tooltips: "툴팁",
        tooltipsDesc: "유용한 툴팁 표시",
        darkMode: "다크 모드",
        lightMode: "라이트 모드",
        fontSizes: {
          small: "작게",
          medium: "중간",
          large: "크게"
        }
      },
      actions: {
        save: "변경사항 저장",
        reset: "기본값으로 재설정",
        close: "닫기",
      },
      notifications: {
        saved: "설정이 성공적으로 저장되었습니다",
        reset: "설정이 기본값으로 재설정되었습니다",
      },
    },
    AR: {
      title: "الإعدادات",
      tabs: {
        accessibility: "إمكانية الوصول",
        interface: "الواجهة",
      },
      accessibility: {
        reducedMotion: "تقليل الحركة",
        reducedMotionDesc: "تقليل الحركات والانتقالات",
        fontSize: "حجم الخط",
        screenReader: "مُحسّن لقارئ الشاشة",
        screenReaderDesc: "توافق محسّن مع تقنيات المساعدة",
        keyboardNav: "التنقل بلوحة المفاتيح",
        keyboardNavDesc: "دعم كامل لإمكانية الوصول بلوحة المفاتيح",
      },
      interface: {
        theme: "المظهر",
        compactMode: "الوضع المدمج",
        compactModeDesc: "تقليل المسافات والهوامش",
        animations: "الحركات",
        animationsDesc: "تفعيل حركات الواجهة",
        notifications: "الإشعارات",
        notificationsDesc: "عرض إشعارات النظام",
        tooltips: "تلميحات",
        tooltipsDesc: "عرض تلميحات مفيدة",
        darkMode: "الوضع الداكن",
        lightMode: "الوضع الفاتح",
        fontSizes: {
          small: "صغير",
          medium: "متوسط",
          large: "كبير"
        }
      },
      actions: {
        save: "حفظ التغييرات",
        reset: "إعادة تعيين الإعدادات الافتراضية",
        close: "إغلاق",
      },
      notifications: {
        saved: "تم حفظ الإعدادات بنجاح",
        reset: "تم إعادة تعيين الإعدادات إلى القيم الافتراضية",
      },
    },
  };

  const currentMessages =
    messages[selectedLanguage as keyof typeof messages] || messages.EN;

  const handleThemeToggle = () => {
    toggleDarkMode();
    updateInterface({
      theme: isDarkMode ? "light" : "dark"
    });
    trackFeatureUsage("settings", "toggle_theme");
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300"
        onClick={onClose} 
      />
      <div 
        className="fixed right-2 top-8 h-[60vh] w-full max-w-xs sm:right-4 sm:top-4 sm:h-[75vh] sm:max-w-sm bg-white dark:bg-gray-900 shadow-2xl z-50 
                   transform transition-transform duration-300 ease-in-out overflow-hidden rounded-2xl px-1 sm:px-0"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-t-2xl">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {currentMessages.title}
            </h2>
            <button
              onClick={onClose}
              className="ml-2 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close settings panel"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-300" />
            </button>
          </div>

          {/* Tabs */}
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(currentMessages.tabs).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key as any)}
                  className={`px-2 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === key
                      ? "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 shadow-sm"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800/50">
            <div className="p-3 space-y-3">
              {activeTab === "interface" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-xl shadow-sm">
                    <div>
                      <h3 className="text-base font-medium text-gray-900 dark:text-white">
                        {currentMessages.interface.theme}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {isDarkMode ? currentMessages.interface.darkMode : currentMessages.interface.lightMode}
                      </p>
                    </div>
                    <button
                      onClick={handleThemeToggle}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                    >
                      {isDarkMode ? (
                        <Sun className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <Moon className="w-5 h-5 text-gray-700" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "accessibility" && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    {currentMessages.tabs.accessibility}
                  </h3>

                  <div className="space-y-3">
                    <div className="p-3 bg-white dark:bg-gray-900 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="block font-medium text-gray-900 dark:text-white">
                            {currentMessages.accessibility.reducedMotion}
                          </label>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {currentMessages.accessibility.reducedMotionDesc}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={accessibility.reducedMotion}
                          onChange={(e) =>
                            updateAccessibility({
                              reducedMotion: e.target.checked,
                            })
                          }
                          className="w-5 h-5 text-teal-600 focus:ring-teal-500 rounded border-gray-300 
                                   dark:border-gray-600 dark:bg-gray-700"
                        />
                      </div>
                    </div>

                    <div className="p-3 bg-white dark:bg-gray-900 rounded-lg shadow-sm">
                      <label className="block font-medium text-gray-900 dark:text-white mb-2">
                        {currentMessages.accessibility.fontSize}
                      </label>
                      <select
                        value={accessibility.fontSize}
                        onChange={(e) =>
                          updateAccessibility({
                            fontSize: e.target.value as
                              | "small"
                              | "medium"
                              | "large",
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 
                                 focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 
                                 transition-colors duration-200"
                      >
                        <option value="small">{currentMessages.interface.fontSizes.small}</option>
                        <option value="medium">{currentMessages.interface.fontSizes.medium}</option>
                        <option value="large">{currentMessages.interface.fontSizes.large}</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="flex justify-end gap-2">
              {/* Removed Close button */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPanel;

